"use client";

import clsx from "clsx";
import Tooltip from "@/components/library/Tooltip";
import Icon from "@/components/library/Icon";
import type { IconName } from "@fortawesome/fontawesome-common-types";

interface SendButtonProps {
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  icon?: `${IconName}` | `lula-${string}` | `custom-${string}`;
  variant?: "primary" | "secondary" | "stop";
  tooltip?: string;
  ariaLabel?: string;
}

const SendButton: React.FC<SendButtonProps> = ({
  disabled = false,
  loading = false,
  className,
  onClick,
  type = "submit",
  icon = "arrow-up",
  variant = "primary",
  tooltip,
  ariaLabel,
}) => {
  const baseClasses =
    "flex items-center justify-center p-2 rounded-xl focus:outline-none h-10 w-10 transition-colors";
  const variantClasses = {
    primary:
      "text-white bg-gailPrimary hover:bg-gailPrimary/80 disabled:bg-transparent disabled:text-300",
    secondary:
      "text-white bg-500 hover:bg-600 disabled:bg-200 disabled:text-400",
    // Muted while streaming: stopping is a secondary action, so it should not
    // compete with the primary send state.
    stop: "text-800 bg-200 hover:bg-300 hover:text-1000",
  };

  const button = (
    <button
      key={`send-button-${loading ? "loading" : "idle"}`}
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={ariaLabel}
      className={clsx(baseClasses, variantClasses[variant], className)}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <Icon icon={icon} />
      )}
    </button>
  );

  if (tooltip && disabled && !loading) {
    return (
      <Tooltip
        content={tooltip}
        placement="top"
        className="w-max max-w-md text-center"
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default SendButton;
